import React, { useState, createContext, useContext } from 'react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Tabs Context for managing active tab state
 */
interface TabsContextType {
  activeTab: string;
  setActiveTab: (value: string) => void;
}

const TabsContext = createContext<TabsContextType | null>(null);

/**
 * Tabs Root Component
 */
interface TabsProps {
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  children?: React.ReactNode;
}

export const Tabs: React.FC<TabsProps> = ({ defaultValue, value, onValueChange, className, children }) => {
  const [activeTab, setActiveTab] = useState(defaultValue || '');
  const { colors } = useTheme();

  const currentValue = value !== undefined ? value : activeTab;

  const handleTabChange = (newValue: string) => {
    if (value === undefined) {
      setActiveTab(newValue);
    }
    onValueChange?.(newValue);
  };

  return (
    <TabsContext.Provider value={{ activeTab: currentValue, setActiveTab: handleTabChange }}>
      <div className={cn('w-full', className)}>{children}</div>
    </TabsContext.Provider>
  );
};

/**
 * TabsList Component - Container for tab triggers
 */
interface TabsListProps {
  className?: string;
  children?: React.ReactNode;
}

export const TabsList: React.FC<TabsListProps> = ({ className, children }) => {
  const { colors } = useTheme();

  return (
    <div className={cn('flex border-b', colors.border, className)}>{children}</div>
  );
};

/**
 * TabsTrigger Component - Individual tab button
 */
interface TabsTriggerProps {
  value: string;
  className?: string;
  disabled?: boolean;
  children?: React.ReactNode;
}

export const TabsTrigger: React.FC<TabsTriggerProps> = ({ value, className, disabled = false, children }) => {
  const context = useContext(TabsContext);
  const { colors } = useTheme();
  
  if (!context) {
    throw new Error('TabsTrigger must be used within Tabs');
  }

  const { activeTab, setActiveTab } = context;
  const isActive = activeTab === value;

  return (
    <button
      type="button"
      onClick={() => !disabled && setActiveTab(value)}
      disabled={disabled}
      className={cn(
        'px-4 py-2 text-sm font-medium transition-all duration-200',
        'border-b-2 border-transparent -mb-px',
        'focus:outline-none focus:ring-2 focus:ring-offset-2',
        isActive && colors.primaryBorderAll,
        isActive ? colors.text : colors.textSecondary,
        !disabled && !isActive && colors.interactiveHover,
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {children}
    </button>
  );
};

/**
 * TabsContent Component - Content panel for each tab
 */
interface TabsContentProps {
  value: string;
  className?: string;
  children?: React.ReactNode;
}

export const TabsContent: React.FC<TabsContentProps> = ({ value, className, children }) => {
  const context = useContext(TabsContext);
  
  if (!context) {
    throw new Error('TabsContent must be used within Tabs');
  }

  const { activeTab } = context;

  if (activeTab !== value) return null;

  return (
    <div
      className={cn(
        'mt-4',
        'focus:outline-none',
        className
      )}
    >
      {children}
    </div>
  );
};

// Default export for backward compatibility
const TabsComponent = {
  Root: Tabs,
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent,
};

export default TabsComponent;

